/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../entities/app.entity";
import { fetchFromApi, handleFetchError } from "./api";
import { HookProps } from "./services.types";
import { removeEmptyKeys } from "../utils/helpers";

export const useApi = ({
  path,
  method = "get",
  initialState = [],
  page = 1,
  perPage,
  queryParams = {},
  searchParams = "",
  enabled = true,
}: HookProps) => {
  const screenWidth = window.screen.width;
  const [requesting, setRequesting] = useState(enabled && Boolean(path));
  const [data, setData] = useState<any>(initialState);
  const [err, setErr] = useState();
  const [meta, setMeta] = useState<any>({});
  const [kPage, setPage] = useState(page || 1);
  const [kPerPage, setPerPage] = useState(
    perPage || (screenWidth >= 640 ? 20 : 50)
  );
  const [kQueryParams, setQueryParams] = useState(queryParams);
  const [searchQuery, setSearchQuery] = useState(searchParams);
  const requestId = useRef(0);
  const searchQueryRef = useRef(searchQuery);
  const hasMountedSearch = useRef(false);
  searchQueryRef.current = searchQuery;

  const fetchData = useCallback(
    async (search?: string) => {
      if (!enabled) return;
      const currentRequestId = ++requestId.current;
      setRequesting(true);
      try {
        const { data } = await fetchFromApi({
          path,
          method,
          params: removeEmptyKeys({
            page: kPage,
            per_page: kPerPage || perPage,
            search_query: search,
            ...kQueryParams,
          }),
        });
        if (currentRequestId !== requestId.current) return;
        if (data) {
          if (data.data) setData(data.data);
          if (data.meta && Object.keys(data.meta).length) setMeta(data.meta);
        }
      } catch (error: any) {
        if (currentRequestId === requestId.current) {
          setErr(error);
          handleFetchError(error);
        }
      } finally {
        if (currentRequestId === requestId.current) setRequesting(false);
      }
    },
    [enabled, kPage, kPerPage, method, path, perPage, kQueryParams]
  );

  useEffect(() => {
    if (!enabled) return;
    fetchData(searchQueryRef.current || undefined);
  }, [enabled, fetchData]);

  const latestFetchData = useRef(fetchData);
  latestFetchData.current = fetchData;
  const handleChangeWithLib = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          await latestFetchData.current(value || undefined);
        } catch (error) {
          toast("An error occurred:", "error");
        }
      }, 500),
    []
  );

  useEffect(() => {
    if (!hasMountedSearch.current) {
      hasMountedSearch.current = true;
      return;
    }
    handleChangeWithLib(searchQuery);
    return () => handleChangeWithLib.cancel();
  }, [handleChangeWithLib, searchQuery]);

  return {
    requesting,
    path,
    data,
    err,
    reFetch: () => fetchData(),
    queryParams: {
      get: kQueryParams,
      set: (val: any) => setQueryParams((prev) => ({ ...prev, ...val })),
    },
    searchParams: {
      get: searchQuery,
      set: (val: any) => setSearchQuery(val),
    },
    paginate: {
      meta,
      page: kPage,
      perPage: kPerPage,
      setPerPage,
      handlePageChange: (value: number) => {
        setPage(value);
        setMeta({ ...meta, currentPage: value });
      },
    },
  };
};
